// /admin/integrations/webhooks — gestione webhook (Sprint 7).

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  WEBHOOK_EVENTS,
  type WebhookEvent,
  type WebhookListItem,
  type WebhookDeliveryRow,
} from '@audiq/shared';
import { IconButton } from '@audiq/ui';
import { History, Trash2, Zap } from 'lucide-react';
import { api } from '../../lib/api';

export function WebhooksPage() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['admin', 'webhooks'], queryFn: api.listWebhooks });
  const [showCreate, setShowCreate] = useState(false);
  const [createdSecret, setCreatedSecret] = useState<{ id: string; secret: string } | null>(null);
  const [deliveriesOpen, setDeliveriesOpen] = useState<string | null>(null);

  const deleteMu = useMutation({
    mutationFn: (id: string) => api.deleteWebhook(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'webhooks'] }),
  });
  const updateMu = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.updateWebhook(id, { is_active: isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'webhooks'] }),
  });
  const testMu = useMutation({
    mutationFn: (id: string) => api.testWebhook(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'webhooks'] }),
  });

  if (list.isLoading)
    return (
      <p className="mono" style={{ color: 'var(--mute)' }}>
        Caricamento…
      </p>
    );

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h3 className="serif text-2xl" style={{ color: 'var(--ink)' }}>
          Webhook configurati
        </h3>
        <button
          onClick={() => setShowCreate(true)}
          className="rounded-md px-4 py-2 text-sm font-medium"
          style={{ background: 'var(--primary)', color: '#fff' }}
        >
          + Nuovo webhook
        </button>
      </div>

      <div
        className="overflow-x-auto rounded-md border"
        style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
      >
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: 'var(--soft)' }}>
              <th className="text-start p-3 mono" style={{ color: 'var(--mute)' }}>
                Nome
              </th>
              <th className="text-start p-3 mono" style={{ color: 'var(--mute)' }}>
                URL
              </th>
              <th className="text-start p-3 mono" style={{ color: 'var(--mute)' }}>
                Eventi
              </th>
              <th className="text-start p-3 mono" style={{ color: 'var(--mute)' }}>
                Last status
              </th>
              <th className="text-start p-3 mono" style={{ color: 'var(--mute)' }}>
                Pending
              </th>
              <th className="text-start p-3 mono" style={{ color: 'var(--mute)' }}>
                Active
              </th>
              <th className="text-end p-3 mono" style={{ color: 'var(--mute)' }}>
                Azioni
              </th>
            </tr>
          </thead>
          <tbody>
            {list.data?.length === 0 && (
              <tr>
                <td colSpan={7} className="p-6 text-center" style={{ color: 'var(--mute)' }}>
                  Nessun webhook configurato.
                </td>
              </tr>
            )}
            {list.data?.map((w: WebhookListItem) => (
              <tr key={w.id} style={{ borderTop: '1px solid var(--line)' }}>
                <td className="p-3" style={{ color: 'var(--ink)' }}>
                  {w.name}
                </td>
                <td className="p-3 mono text-xs break-all">{w.url}</td>
                <td className="p-3 text-xs">
                  {w.events.map((e) => (
                    <span key={e} className="me-1 mono" style={{ color: 'var(--mute)' }}>
                      {e}
                    </span>
                  ))}
                </td>
                <td className="p-3 text-xs">
                  {w.last_delivery_status ? (
                    <span
                      style={{
                        color:
                          w.last_delivery_status === 'delivered'
                            ? 'var(--primary-2)'
                            : w.last_delivery_status === 'exhausted'
                              ? 'var(--danger)'
                              : 'var(--mute)',
                      }}
                    >
                      ● {w.last_delivery_status}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--mute)' }}>—</span>
                  )}
                </td>
                <td className="p-3 text-xs" style={{ color: 'var(--mute)' }}>
                  {w.pending_count ?? 0}
                </td>
                <td className="p-3">
                  <input
                    type="checkbox"
                    checked={w.is_active}
                    onChange={(e) => updateMu.mutate({ id: w.id, isActive: e.target.checked })}
                  />
                </td>
                <td className="p-3 text-end">
                  <div className="flex items-center justify-end gap-1">
                    <IconButton icon={Zap} label="Test" onClick={() => testMu.mutate(w.id)} />
                    <IconButton
                      icon={History}
                      label="Storia"
                      onClick={() => setDeliveriesOpen(w.id)}
                    />
                    <IconButton
                      icon={Trash2}
                      label="Elimina"
                      tone="danger"
                      onClick={() => {
                        if (window.confirm(`Eliminare definitivamente "${w.name}"?`)) {
                          deleteMu.mutate(w.id);
                        }
                      }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {testMu.data && (
        <div className="mt-3 text-xs" style={{ color: 'var(--mute)' }}>
          Test result: {JSON.stringify(testMu.data.result)}
        </div>
      )}

      {showCreate && (
        <CreateWebhookDialog
          onClose={() => setShowCreate(false)}
          onCreated={(id, secret) => {
            setCreatedSecret({ id, secret });
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: ['admin', 'webhooks'] });
          }}
        />
      )}

      {createdSecret && (
        <CreatedSecretDialog secret={createdSecret.secret} onClose={() => setCreatedSecret(null)} />
      )}

      {deliveriesOpen && (
        <DeliveriesDrawer webhookId={deliveriesOpen} onClose={() => setDeliveriesOpen(null)} />
      )}
    </div>
  );
}

function CreateWebhookDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string, secret: string) => void;
}) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<WebhookEvent[]>(['encounter.completed']);

  const createMu = useMutation({
    mutationFn: () => api.createWebhook({ name, url, events }),
    onSuccess: (resp) => onCreated(resp.webhook.id, resp.raw_secret),
  });

  return (
    <div
      role="dialog"
      aria-modal
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        className="rounded-md p-6 w-full max-w-md"
        style={{ background: 'var(--paper)', border: '1px solid var(--line)' }}
      >
        <h3 className="serif text-2xl mb-4" style={{ color: 'var(--ink)' }}>
          Nuovo webhook
        </h3>

        <label className="block mb-3 text-sm" style={{ color: 'var(--ink)' }}>
          Nome
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded-md border px-3 py-2"
            style={{
              background: 'var(--surface)',
              borderColor: 'var(--line)',
              color: 'var(--ink)',
            }}
          />
        </label>

        <label className="block mb-3 text-sm" style={{ color: 'var(--ink)' }}>
          URL (https://…)
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="mt-1 w-full rounded-md border px-3 py-2"
            style={{
              background: 'var(--surface)',
              borderColor: 'var(--line)',
              color: 'var(--ink)',
            }}
          />
        </label>

        <div className="mb-4 text-sm" style={{ color: 'var(--ink)' }}>
          <div className="mb-2 mono" style={{ color: 'var(--mute)' }}>
            Eventi:
          </div>
          {WEBHOOK_EVENTS.filter((e) => e !== 'test.ping').map((e) => (
            <label key={e} className="block">
              <input
                type="checkbox"
                checked={events.includes(e)}
                onChange={(ev) => {
                  if (ev.target.checked) setEvents([...events, e]);
                  else setEvents(events.filter((x) => x !== e));
                }}
                className="me-2"
              />
              <span className="mono text-xs">{e}</span>
            </label>
          ))}
        </div>

        {createMu.error && (
          <p className="text-sm mb-2" style={{ color: 'var(--danger)' }}>
            {String(createMu.error)}
          </p>
        )}

        <div className="flex gap-2 justify-end">
          <button
            onClick={onClose}
            className="rounded-md px-3 py-2 text-sm"
            style={{ color: 'var(--mute)' }}
          >
            Annulla
          </button>
          <button
            disabled={!name || !url || events.length === 0 || createMu.isPending}
            onClick={() => createMu.mutate()}
            className="rounded-md px-4 py-2 text-sm"
            style={{
              background: 'var(--primary)',
              color: '#fff',
              opacity: createMu.isPending ? 0.6 : 1,
            }}
          >
            {createMu.isPending ? 'Creazione…' : 'Crea webhook'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreatedSecretDialog({ secret, onClose }: { secret: string; onClose: () => void }) {
  return (
    <div
      role="dialog"
      aria-modal
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        className="rounded-md p-6 w-full max-w-lg"
        style={{ background: 'var(--paper)', border: '1px solid var(--line)' }}
      >
        <h3 className="serif text-2xl mb-2" style={{ color: 'var(--ink)' }}>
          Secret webhook
        </h3>
        <p className="text-sm mb-4" style={{ color: 'var(--mute)' }}>
          <strong style={{ color: 'var(--danger)' }}>Salva questo secret ora.</strong> Sarà
          necessario al consumer per verificare la firma HMAC.
        </p>
        <div
          className="rounded-md p-3 mono text-sm break-all"
          style={{ background: 'var(--soft)', color: 'var(--ink)' }}
        >
          {secret}
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={onClose}
            className="rounded-md px-4 py-2 text-sm"
            style={{ background: 'var(--primary)', color: '#fff' }}
          >
            Ho salvato il secret
          </button>
        </div>
      </div>
    </div>
  );
}

function DeliveriesDrawer({ webhookId, onClose }: { webhookId: string; onClose: () => void }) {
  const list = useQuery({
    queryKey: ['admin', 'webhook-deliveries', webhookId],
    queryFn: () => api.listWebhookDeliveries(webhookId),
  });
  const qc = useQueryClient();
  const retryMu = useMutation({
    mutationFn: (deliveryId: string) => api.retryWebhookDelivery(webhookId, deliveryId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'webhook-deliveries', webhookId] }),
  });

  return (
    <div
      role="dialog"
      aria-modal
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-end',
        zIndex: 1000,
      }}
    >
      <div
        className="h-full overflow-y-auto p-6"
        style={{
          background: 'var(--paper)',
          width: '50%',
          minWidth: 500,
          borderLeft: '1px solid var(--line)',
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="serif text-2xl" style={{ color: 'var(--ink)' }}>
            Storia consegne
          </h3>
          <button onClick={onClose} className="text-sm" style={{ color: 'var(--mute)' }}>
            ✕ Chiudi
          </button>
        </div>
        {list.isLoading && (
          <p className="mono" style={{ color: 'var(--mute)' }}>
            Caricamento…
          </p>
        )}
        <ul className="space-y-2">
          {list.data?.map((d: WebhookDeliveryRow) => (
            <li
              key={d.id}
              className="rounded-md border p-3 text-xs"
              style={{ background: 'var(--surface)', borderColor: 'var(--line)' }}
            >
              <div className="flex justify-between items-center">
                <span className="mono" style={{ color: 'var(--ink)' }}>
                  {d.event_type}
                </span>
                <span
                  style={{
                    color:
                      d.status === 'delivered'
                        ? 'var(--primary-2)'
                        : d.status === 'exhausted'
                          ? 'var(--danger)'
                          : 'var(--mute)',
                  }}
                >
                  ● {d.status}
                </span>
              </div>
              <div className="mono mt-1" style={{ color: 'var(--mute)' }}>
                {new Date(d.created_at).toLocaleString('it-IT')} · attempts: {d.attempts}
                {d.response_status ? ` · http ${d.response_status}` : ''}
              </div>
              {d.response_body_excerpt && (
                <div
                  className="mt-2 p-2 rounded-md mono text-xs break-all"
                  style={{ background: 'var(--soft)', color: 'var(--ink)' }}
                >
                  {d.response_body_excerpt}
                </div>
              )}
              {(d.status === 'failed' || d.status === 'exhausted') && (
                <button
                  onClick={() => retryMu.mutate(d.id)}
                  className="mt-2 text-xs"
                  style={{ color: 'var(--primary-2)' }}
                  disabled={retryMu.isPending}
                >
                  {retryMu.isPending ? 'Retry…' : 'Riprova ora'}
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
